import { useEffect, useState } from "react";
import {
  Col,
  Row,
  Input,
  Form,
  Checkbox,
  Button,
  DatePicker,
  TimePicker,
  Select,
} from "antd";
import { BASE_COLORS } from "config/theme/constants";
import { BaseButtonsForm } from "components/common/forms/BaseButtonsForm";
import PhoneInputComponent from "components/common/PhoneInputComponent";
import { notificationController } from "components/common/notificationController";
import SpinLoader from "components/Loader/SpinLoader";
import { useDispatch } from "react-redux";
import { config } from "config/site.config";
import "react-phone-input-2/lib/style.css";
import { addUpdatePiaggioCustomerRequest } from "redux/piaggioCustomers/actions";
import dayjs from "dayjs";
import {
  DATE_TIME_FORMAT_WITHOUT_SECONDS_24HRS,
  disablePastDates,
  SEVER_DATE_TIME_FORMAT,
} from "library/helpers/utils";
import { PiaggioCustomers } from "redux/piaggioCustomers/types";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  piaggioCustomersString,
  stringConfig,
} from "config/site.string";
import { ServiceLevel, serviceLevels } from "utils/utils";
const { TextArea } = Input;

dayjs.extend(utc);
dayjs.extend(timezone);
interface IAddUpdateCustomerForm {
  form: any;
  handleOk: any;
  isEdit: boolean;
  onClose: (isSuccess?: boolean) => void;
  handleCancel: () => void;
  selectedRecord: PiaggioCustomers;
}

const EditPiaggioCustomers = ({
  form,
  isEdit,
  onClose,
  selectedRecord,
}: IAddUpdateCustomerForm) => {
  const dispatch = useDispatch();
  const [consigneePhoneData, setConsigneePhoneData] = useState<any>({
    phoneValue: "",
  });
  const [shipperPhoneData, setShipperPhoneData] = useState<any>({
    phoneValue: "",
  });
  const [pending] = useState<boolean>(false);
  const currentUser = JSON.parse(
    localStorage.getItem("loggedInUser") || "null",
  );

  const labelStyle: React.CSSProperties = {
    minWidth: "200px",
    textAlign: "left",
  };

  useEffect(() => {
    if (!isEdit) {
      form.resetFields();
    } else if (selectedRecord) {
      // Set consignee phone data
      const consigneePhone = selectedRecord?.consigneePhone?.toString();
      setConsigneePhoneData({
        ...consigneePhoneData,
        phoneNumber: consigneePhone || "",
        phoneValue: consigneePhone || "",
      });
      const shipperPhone = selectedRecord?.shipperPhone?.toString();
      setShipperPhoneData({
        ...shipperPhoneData,
        phoneNumber: shipperPhone || "",
        phoneValue: shipperPhone || "",
      });

      form.setFieldsValue({
        pickupDateTime: selectedRecord?.pickupDateTime,
        pickupCloseDateTime: selectedRecord?.pickupCloseDateTime,
        scheduledDeliveryDateTime: selectedRecord?.scheduledDeliveryDateTime,
        consigneeReference: selectedRecord?.consigneeReference || "",
        consigneeName: selectedRecord?.consigneeName || "",
        consigneeContact: selectedRecord?.consigneeContact || "",
        consigneeAddress1: selectedRecord?.consigneeAddress1 || "",
        consigneeAddress2: selectedRecord?.consigneeAddress2 || "",
        consigneeCity: selectedRecord?.consigneeCity || "",
        consigneeState: selectedRecord?.consigneeState || "",
        consigneeZip: selectedRecord?.consigneeZip || "",
        consigneeCountry: selectedRecord?.consigneeCountry || "",
        shipperName: selectedRecord?.shipperName || "",
        shipperContact: selectedRecord?.shipperContact || "",
        shipperAddress1: selectedRecord?.shipperAddress1 || "",
        shipperAddress2: selectedRecord?.shipperAddress2 || "",
        shipperCity: selectedRecord?.shipperCity || "",
        shipperState: selectedRecord?.shipperState || "",
        shipperZip: selectedRecord?.shipperZip || "",
        shipperCountry: selectedRecord?.shipperCountry || "",
        isHazmat: selectedRecord?.isHazmat === 1,
        specialInstructions: selectedRecord?.specialInstructions || "",
        serviceLevel: selectedRecord?.serviceLevel,
      });
    }
  }, [isEdit, selectedRecord, form]);

  const addCustomersCallback = (type: string, message: string) => {
    if (type === "Success") {
      notificationController.success({
        message: "Success",
        description: message
          ? message
          : `${
              isEdit
                ? `Piaggio Address details updated successfully`
                : `Piaggio Address details added successfully`
            }`,
        placement: "topRight",
      });
      form.resetFields();
      onClose(true);
    } else {
      notificationController.error({
        message: "Error",
        description: message ? message : "Sorry an error occurred",
        placement: "topRight",
      });
    }
  };

  const handleSubmit = (values: any) => {
    const customerValues: PiaggioCustomers = {
      id: isEdit ? selectedRecord?.id : undefined,
      consigneeReference: values.consigneeReference,
      serviceLevel: values?.serviceLevel,
      consigneeName: values.consigneeName,
      consigneeContact: values.consigneeContact,
      consigneePhone: consigneePhoneData.phoneValue
        ? consigneePhoneData.phoneValue.replace(/-/g, "")
        : null,
      consigneeAddress1: values.consigneeAddress1,
      consigneeAddress2: values.consigneeAddress2,
      consigneeCity: values.consigneeCity,
      consigneeState: values.consigneeState,
      consigneeZip: values.consigneeZip,
      consigneeCountry: values.consigneeCountry,
      shipperName: values.shipperName,
      shipperContact: values.shipperContact,
      shipperPhone: shipperPhoneData.phoneValue
        ? shipperPhoneData.phoneValue.replace(/-/g, "")
        : null,
      shipperAddress1: values.shipperAddress1,
      shipperAddress2: values.shipperAddress2,
      shipperCity: values.shipperCity,
      shipperState: values.shipperState,
      shipperZip: values.shipperZip,
      shipperCountry: values.shipperCountry,
      isHazmat: values.isHazmat ? 1 : 0,
      specialInstructions: values.specialInstructions,
      createdBy: currentUser?.userDetails?.userId,
      pickupDateTime: values.pickupDateTime?.format(SEVER_DATE_TIME_FORMAT),
      pickupCloseDateTime: values.pickupCloseDateTime?.format(
        SEVER_DATE_TIME_FORMAT,
      ),
      scheduledDeliveryDateTime: values.scheduledDeliveryDateTime?.format(
        SEVER_DATE_TIME_FORMAT,
      ),
    };
    dispatch(
      addUpdatePiaggioCustomerRequest(
        config.addUpdatePiaggioCustomer,
        customerValues,
        addCustomersCallback,
      ),
    );
  };

  const handleCancelButtonClick = () => {
    form.resetFields();
    onClose();
  };

  const SelectServiceLevelDropdown = (
    serviceLevels: ServiceLevel[] | undefined,
    value: string | null,
    onChange: ((value: string) => void) | undefined,
    placeholder: string,
  ) => {
    return (
      <Select
        style={{ width: "100%" }}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        options={serviceLevels?.map((level) => ({
          value: level.serviceLevelCode,
          label: level.serviceLevelDescription,
        }))}
      />
    );
  };

  return (
    <>
      {pending ? (
        <SpinLoader />
      ) : (
        <BaseButtonsForm
          form={form}
          name="piaggioCustomer"
          layout="horizontal"
          style={{ padding: "12px 20px 0px" }}
          onFinish={handleSubmit}
          id="piaggioCustomer"
        >
          <Row gutter={{ xs: 10, md: 15, xl: 30 }}>
            {/* Consignee Reference */}
            <Col xs={24} md={12}>
              <Form.Item
                name="consigneeReference"
                label="Consignee Reference"
                labelCol={{ style: labelStyle }}
              >
                <Input disabled={isEdit} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}></Col> {/* No shipper equivalent */}
            {/* Name */}
            <Col xs={24} md={12}>
              <Form.Item
                name="consigneeName"
                label="Consignee Name"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="shipperName"
                label="Shipper Name"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            {/* Contact */}
            <Col xs={24} md={12}>
              <Form.Item
                name="consigneeContact"
                label="Consignee Contact"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="shipperContact"
                label="Shipper Contact"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            {/* Phone */}
            <Col xs={24} md={12}>
              <Form.Item
                name="consigneePhone"
                label="Consignee Phone"
                labelCol={{ style: labelStyle }}
              >
                <PhoneInputComponent
                  form={form}
                  phoneData={consigneePhoneData}
                  setPhoneData={setConsigneePhoneData}
                  country="us"
                  countriesList={["ca", "mx", "us"]}
                  fieldName="consigneePhone"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="shipperPhone"
                label="Shipper Phone"
                labelCol={{ style: labelStyle }}
              >
                <PhoneInputComponent
                  form={form}
                  phoneData={shipperPhoneData}
                  setPhoneData={setShipperPhoneData}
                  country="us"
                  countriesList={["ca", "mx", "us"]}
                  fieldName="shipperPhone"
                />
              </Form.Item>
            </Col>
            {/* Address 1 */}
            <Col xs={24} md={12}>
              <Form.Item
                name="consigneeAddress1"
                label="Consignee Address 1"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="shipperAddress1"
                label="Shipper Address 1"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            {/* Address 2 */}
            <Col xs={24} md={12}>
              <Form.Item
                name="consigneeAddress2"
                label="Consignee Address 2"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="shipperAddress2"
                label="Shipper Address 2"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            {/* City */}
            <Col xs={24} md={12}>
              <Form.Item
                name="consigneeCity"
                label="Consignee City"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="shipperCity"
                label="Shipper City"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            {/* State */}
            <Col xs={24} md={12}>
              <Form.Item
                name="consigneeState"
                label="Consignee State"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="shipperState"
                label="Shipper State"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            {/* Zip */}
            <Col xs={24} md={12}>
              <Form.Item
                name="consigneeZip"
                label="Consignee Zip"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="shipperZip"
                label="Shipper Zip"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            {/* Country */}
            <Col xs={24} md={12}>
              <Form.Item
                name="consigneeCountry"
                label="Consignee Country"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="shipperCountry"
                label="Shipper Country"
                labelCol={{ style: labelStyle }}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={piaggioCustomersString.pickupDateTime}
                name="pickupDateTime"
                labelCol={{ style: labelStyle }}
              >
                <DatePicker
                  format={DATE_TIME_FORMAT_WITHOUT_SECONDS_24HRS}
                  style={{ width: "100%" }}
                  disabledDate={(current) => disablePastDates(current)}
                  showTime
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={piaggioCustomersString.pickupCloseDateTime}
                name="pickupCloseDateTime"
                labelCol={{ style: labelStyle }}
              >
                <TimePicker format="HH:mm" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={piaggioCustomersString.scheduledDeliveryDateTime}
                name="scheduledDeliveryDateTime"
                labelCol={{ style: labelStyle }}
              >
                <DatePicker
                  format={DATE_TIME_FORMAT_WITHOUT_SECONDS_24HRS}
                  style={{ width: "100%" }}
                  disabledDate={(current) => disablePastDates(current)}
                  showTime
                />
              </Form.Item>
            </Col>
            {/* Hazmat */}
            <Col xs={24} md={24}>
              <Form.Item
                name="isHazmat"
                label="Hazmat"
                valuePropName="checked"
                labelCol={{ style: labelStyle }}
              >
                <Checkbox className="checkboxColorClass" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <BaseButtonsForm.Item
                name="serviceLevel"
                label="Service Level"
                labelCol={{ style: labelStyle }}
              >
                {SelectServiceLevelDropdown(
                  serviceLevels,
                  "5D",
                  undefined,
                  stringConfig.select,
                )}
              </BaseButtonsForm.Item>
            </Col>
            {/* Special Instructions */}
            <Col xs={24} md={24}>
              <Form.Item
                name="specialInstructions"
                label="Special Instructions"
                labelCol={{ style: labelStyle }}
                rules={[
                  {
                    max: 2000,
                    message: "Maximum 2000 characters can be entered",
                  },
                ]}
              >
                <TextArea
                  count={{ show: true, max: 2000 }}
                  className="no-resize"
                  style={{ border: `1px solid ${BASE_COLORS.altolight}` }}
                  rows={4}
                />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ textAlign: "right", marginTop: 10 }}>
            <Button type="primary" htmlType="submit" style={{ marginRight: 5 }}>
              SUBMIT
            </Button>
            <Button type="primary" onClick={handleCancelButtonClick}>
              CANCEL
            </Button>
          </div>
        </BaseButtonsForm>
      )}
    </>
  );
};

export default EditPiaggioCustomers;
